/* eslint-disable no-console */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { parseMetadata, isFeatureEnabled } from 'shared/helpers';
import { platformFeatures } from 'shared/constants';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  TextInput,
  TextArea,
  PhoneNumberInput,
} from 'shared/components';
import { validateJSON, debouncedValidateJSON } from 'shared/validation';
import { patchCustomer } from 'customer/actions';
import './styles.scss';
import { useTranslation } from 'react-i18next';

const CustomerForm = ({ closeForm, customer, updateCustomer }) => {
  const { t } = useTranslation();
  const isMetadataFeatureEnabled = isFeatureEnabled(
    platformFeatures.extra_metadata,
  );

  const initialEmail = get(customer, 'email');

  const [isLoading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);

  const [customerDetails, setCustomerDetails] = useState({
    email: get(customer, 'email', ''),
    firstName: get(customer, 'first_name', ''),
    lastName: get(customer, 'last_name', ''),
    phoneNumber: get(customer, 'phone', ''),
    address: get(customer, 'address', ''),
    city: get(customer, 'city', ''),
    state: get(customer, 'state', ''),
    country: get(customer, 'country', ''),
    zipCode: get(customer, 'postcode', ''),
    companyName: get(customer, 'company_name', ''),
    reference: get(customer, 'reference', ''),
    metadata: parseMetadata(get(customer, 'metadata', '')),
  });

  const [customerErrors, setCustomerErrors] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    companyName: '',
    reference: '',
    metadata: '',
  });

  const updateCustomerDetail = (key, value) => {
    setDirty(true);
    setCustomerDetails((prev) => ({ ...prev, [key]: value }));
  };

  const updateCustomerError = (key, error) => {
    setCustomerErrors((prev) => ({ ...prev, [key]: error }));
  };

  const isFormValid = async () => {
    let errors;
    try {
      errors = await validateJSON(customerDetails.metadata);
      updateCustomerError('metadata', errors);
    } catch (err) {
      console.error(err);
    }
    return !errors;
  };

  const handleSubmit = async () => {
    if (!dirty) {
      closeForm();
      return false;
    }
    const isValid = await isFormValid();
    if (!isValid || isLoading) {
      return false;
    }

    setLoading(true);
    const customerID = get(customer, 'id');
    const isEmailChanged = initialEmail !== customerDetails.email;
    const data = {
      email: isEmailChanged ? customerDetails.email : undefined,
      first_name: customerDetails.firstName,
      last_name: customerDetails.lastName,
      address: customerDetails.address,
      city: customerDetails.city,
      state: customerDetails.state,
      country: customerDetails.country,
      postcode: customerDetails.zipCode,
      phone: customerDetails.phoneNumber || '',
      company_name: customerDetails.companyName,
      reference: customerDetails.reference,
      metadata: customerDetails.metadata
        ? JSON.parse(customerDetails.metadata)
        : {},
    };

    try {
      await patchCustomer(customerID, data);
      Notification('success', t('Changes saved successfully'));
      updateCustomer();
      closeForm();
    } catch (err) {
      console.error(err);
      setLoading(false);
      Notification(
        'error',
        t('Your changes were not saved'),
        t('There was an error while saving your changes'),
      );
    }
    return true;
  };

  const handleClose = () => {
    if (!dirty) {
      return closeForm();
    }
    return setDirtyFormAlertDisplay(true);
  };

  const title = customer
    ? `${t('Edit customer')} - ${
        get(customer, 'email') ||
        get(customer, 'company_name') ||
        get(customer, 'reference')
      }`
    : t('Add customer');

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={title}
      size="lg"
    >
      <div className="CustomerForm">
        <form onSubmit={handleSubmit}>
          <div className="form-inner">
            <div className="left">
              <div>
                <Label text={t('Email')} inputId="customer-email" />
                <TextInput
                  id="customer-email"
                  value={customerDetails.email}
                  error={customerErrors.email}
                  handleChange={(val) => updateCustomerDetail('email', val)}
                />
              </div>
              <div>
                <Label text={t('First Name')} inputId="customer-firstname" />
                <TextInput
                  id="customer-firstname"
                  value={customerDetails.firstName}
                  error={customerErrors.firstName}
                  handleChange={(val) => updateCustomerDetail('firstName', val)}
                />
              </div>
              <div>
                <Label text={t('Last Name')} inputId="customer-lastname" />
                <TextInput
                  id="customer-lastname"
                  value={customerDetails.lastName}
                  error={customerErrors.lastName}
                  handleChange={(val) => updateCustomerDetail('lastName', val)}
                />
              </div>
              <div>
                <Label text={t('Address')} inputId="customer-address" />
                <TextInput
                  id="customer-address"
                  value={customerDetails.address}
                  error={customerErrors.address}
                  handleChange={(val) => updateCustomerDetail('address', val)}
                />
              </div>
              <div>
                <Label text={t('City')} inputId="customer-city" />
                <TextInput
                  id="customer-city"
                  value={customerDetails.city}
                  error={customerErrors.city}
                  handleChange={(val) => updateCustomerDetail('city', val)}
                />
              </div>
              <div>
                <Label text={t('State / Province')} inputId="customer-state" />
                <TextInput
                  id="customer-state"
                  value={customerDetails.state}
                  error={customerErrors.state}
                  handleChange={(val) => updateCustomerDetail('state', val)}
                />
              </div>
            </div>
            <div className="divider active" />
            <div className="right">
              <div>
                <Label text={t('Country')} inputId="customer-country" />
                <TextInput
                  id="customer-country"
                  value={customerDetails.country}
                  error={customerErrors.country}
                  handleChange={(val) => updateCustomerDetail('country', val)}
                />
              </div>
              <div>
                <Label
                  text={t('Zipcode / Postcode')}
                  inputId="customer-zipcode"
                />
                <TextInput
                  id="customer-zipcode"
                  value={customerDetails.zipCode}
                  error={customerErrors.zipCode}
                  handleChange={(val) => updateCustomerDetail('zipCode', val)}
                />
              </div>
              <div>
                <Label
                  text={t('Phone Number')}
                  inputId="customer-phonenumber"
                />
                <PhoneNumberInput
                  value={customerDetails.phoneNumber}
                  error={customerErrors.phoneNumber}
                  handleChange={(val) =>
                    updateCustomerDetail('phoneNumber', val)
                  }
                />
              </div>
              <div>
                <Label text={t('Company Name')} inputId="customer-company" />
                <TextInput
                  id="customer-company"
                  value={customerDetails.companyName}
                  error={customerErrors.companyName}
                  handleChange={(val) =>
                    updateCustomerDetail('companyName', val)
                  }
                />
              </div>
              <div>
                <Label text={t('Reference')} inputId="customer-reference" />
                <TextInput
                  id="customer-reference"
                  value={customerDetails.reference}
                  error={customerErrors.reference}
                  handleChange={(val) => updateCustomerDetail('reference', val)}
                />
              </div>
              <div>
                <Label inputId="metadata-input" text={t('Metadata JSON')} />
                <TextArea
                  handleChange={(val) => {
                    updateCustomerDetail('metadata', val);
                    debouncedValidateJSON(val).then((err) =>
                      updateCustomerError('metadata', err),
                    );
                  }}
                  id="metadata-input"
                  type="metadata"
                  value={customerDetails.metadata}
                  error={customerErrors.metadata}
                  rows="4"
                  disabled={!isMetadataFeatureEnabled}
                />
              </div>
            </div>
          </div>
        </form>
        {isDirtyFormAlertDisplayed && (
          <DirtyFormAlert
            dirty={dirty}
            closeAlert={() => setDirtyFormAlertDisplay(false)}
            closeCb={closeForm}
          />
        )}
      </div>
    </Modal>
  );
};

CustomerForm.propTypes = {
  closeForm: PropTypes.func.isRequired,
  customer: PropTypes.object,
  updateCustomer: PropTypes.func.isRequired,
};

CustomerForm.defaultProps = {
  customer: null,
};

export default CustomerForm;
